import UploadFileIcon from '@mui/icons-material/UploadFile'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import type { DocumentDto } from '@lawyer/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import { ErrorAlert, LoadingBlock } from '../common/Feedback'
import { DocumentList } from './DocumentList'
import { FileDropZone } from './FileDropZone'
import { UploadDocumentDialog, type UploadScope } from './UploadDocumentDialog'

type Props = {
  scope: UploadScope
  emptyText?: string
  /** Added to each document's name, so downloaded files of different clients tell apart. */
  ownerName?: string
}

/** Documents for a single client or task. Cases use `CaseDocumentsPanel` instead. */
export function DocumentsPanel({ scope, emptyText, ownerName }: Props) {
  const queryClient = useQueryClient()
  /** Non-null while the upload dialog is open, carrying a file dropped on the panel. */
  const [pending, setPending] = useState<{ file: File | null } | null>(null)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.documents(scope),
    queryFn: () => api.get<DocumentDto[]>('/documents', { ...scope }),
  })

  const refresh = () => {
    void refetch()
    void queryClient.invalidateQueries({ queryKey: ['case-documents'] })
  }

  return (
    <FileDropZone onFile={(file) => setPending({ file })}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button startIcon={<UploadFileIcon />} onClick={() => setPending({ file: null })}>
          העלאת מסמך
        </Button>
      </Stack>

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}
      {isPending ? <LoadingBlock /> : null}
      {data ? <DocumentList documents={data} emptyText={emptyText} onChanged={refresh} /> : null}

      <UploadDocumentDialog
        open={pending !== null}
        scope={scope}
        ownerName={ownerName}
        defaultFile={pending?.file ?? null}
        onClose={() => setPending(null)}
        onUploaded={refresh}
      />
    </FileDropZone>
  )
}
